import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { History } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useDashboardAssistantDock } from '../../../../contexts/DashboardAssistantDockContext';
import type { DashboardAssistantConversationSummary } from '../types';
import { dropdownMotion } from '../lib/panelLayout';
import {
  getConversationTimestamp,
  getRelativeTimeLabel,
  groupConversations,
} from '../lib/conversationHistory';

type DashboardAssistantChatHistoryMenuProps = {
  rootClassName?: string;
  triggerClassName?: string;
  iconClassName?: string;
};

export function DashboardAssistantChatHistoryMenu({
  rootClassName = 'relative',
  triggerClassName = 'flex h-8 w-8 items-center justify-center rounded-lg border border-edge/80 bg-white text-ink shadow-sm transition hover:bg-paper-light',
  iconClassName = 'h-3.5 w-3.5 text-ink-muted',
}: DashboardAssistantChatHistoryMenuProps) {
  const { openHistoryConversation, sessions } = useDashboardAssistantDock();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isOpen]);

  const conversations = useMemo<DashboardAssistantConversationSummary[]>(
    () =>
      sessions
        .filter(
          (session) =>
            (session.entryCount ?? 0) > 0 || (session.historyMessages?.length ?? 0) > 0,
        )
        .map((session) => ({
          id: session.conversationId || session.id,
          title: session.title,
          startedAt: new Date(session.createdAt).toISOString(),
          lastMessageAt: session.lastMessageAt || null,
          messageCount: session.entryCount ?? session.historyMessages?.length ?? 0,
        }))
        .sort(
          (left, right) =>
            new Date(getConversationTimestamp(right) || 0).getTime() -
            new Date(getConversationTimestamp(left) || 0).getTime(),
        ),
    [sessions],
  );

  const groupedConversations = useMemo(() => groupConversations(conversations), [conversations]);

  const handleOpenConversation = (conversation: DashboardAssistantConversationSummary) => {
    const session = sessions.find(
      (item) => item.id === conversation.id || item.conversationId === conversation.id,
    );
    if (!session) {
      return;
    }

    openHistoryConversation({
      id: session.conversationId || session.id,
      title: session.title || conversation.title || 'Previous chat',
      lastMessageAt: session.lastMessageAt,
      messages: session.historyMessages ?? [],
    });
    setIsOpen(false);
  };

  return (
    <div className={rootClassName} ref={menuRef}>
      <AnimatePresence>
        {isOpen ? (
          <motion.div
            {...dropdownMotion}
            className="absolute bottom-full right-0 z-[96] mb-2 w-[min(390px,calc(100vw-2rem))] origin-bottom-right overflow-hidden rounded-xl border border-edge/80 bg-white text-sm text-ink shadow-[0_18px_60px_rgba(27,32,50,0.16)]"
          >
            <div className="border-b border-edge/70 px-4 py-3 text-sm text-ink-muted">
              Chat history
            </div>

            <div className="max-h-[520px] overflow-y-auto">
              {groupedConversations.length === 0 ? (
                <div className="px-4 py-5 text-sm text-ink-muted">No previous chats yet.</div>
              ) : (
                groupedConversations.map((group) => (
                  <div key={group.label} className="border-b border-edge/70 last:border-b-0">
                    <div className="px-4 py-2 text-xs font-medium text-ink-muted">
                      {group.label}
                    </div>
                    <div className="pb-2">
                      {group.conversations.map((conversation) => (
                        <button
                          key={conversation.id}
                          onClick={() => handleOpenConversation(conversation)}
                          className="flex w-full items-center gap-3 px-6 py-2 text-left text-sm transition hover:bg-paper-light"
                          type="button"
                        >
                          <span className="min-w-0 flex-1 truncate">
                            {conversation.title || 'Untitled chat'}
                          </span>
                          <span className="shrink-0 text-xs text-ink-muted">
                            {getRelativeTimeLabel(getConversationTimestamp(conversation))}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={() => setIsOpen((current) => !current)}
            className={triggerClassName}
            aria-label="Open chat history"
            type="button"
          >
            <History className={iconClassName} />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top">Open chat history</TooltipContent>
      </Tooltip>
    </div>
  );
}
